import Search from "@/assets/comman/Search";
import InputField from "../shared/inputs/InputField";
import BaseModal from "../shared/modals/BaseModal";
import { useState } from "react";
import { CheckboxField } from "../shared/inputs/CheckboxField";
import SelectInput from "../shared/inputs/SelectInput";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import { Calendar28 } from "../shared/inputs/Calendar28";
import useQueryParams from "@/hooks/useQueryParams";
import { useTranslation } from "react-i18next";

export function FiltersModal({ open, onOpenChange, table }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState([]);
  const { setParams } = useQueryParams();

  const handleSubmit = () => {
    const filterMap = filter.reduce((acc, item) => {
      if (item.value !== undefined && item.value !== "") {
        acc[item.accessorKey] = item.value;
      }
      return acc;
    }, {});

    setParams(filterMap);
    onOpenChange(false);
  };

  const handleDelete = (accessorKey) => {
    setFilter((prev) =>
      prev.filter((item) => item.accessorKey !== accessorKey),
    );
  };

  const handleAddFilter = (field) => {
    setFilter((prev) => {
      const exists = prev.find(
        (item) => item.accessorKey === field.accessorKey,
      );
      if (exists) return prev;

      return [
        ...prev,
        {
          accessorKey: field.accessorKey,
          header: field.header,
          type: field.type,
          options: field.options || [],
          value: "",
        },
      ];
    });
  };

  const handleChangeValue = (accessorKey, value) => {
    setFilter((prev) =>
      prev.map((item) =>
        item.accessorKey === accessorKey ? { ...item, value } : item,
      ),
    );
  };
  const handleCancel = () => {
    const clearedParams = filter.reduce((acc, item) => {
      acc[item.accessorKey] = undefined;
      return acc;
    }, {});

    setFilter([]);
    setParams(clearedParams);
  };

  return (
    <BaseModal
      open={open}
      onClose={() => onOpenChange(false)}
      onConfirm={handleSubmit}
      title={t("common.filtersModal.title")}
      size="lg"
      classNameBody={"h-95 scroll-y"}
      textBtn={t("common.filtersModal.apply")}
      textCancel={t("common.filtersModal.clear")}
      onCancel={handleCancel}
    >
      <>
        {/* Search */}
        <div className="relative w-full mt-2">
          <InputField
            className="mb-4 px-8 rounded-[16px] shadow-light-sm w-full"
            placeholder={t("common.filtersModal.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        </div>

        <h4 className="text-md font-medium uppercase">
          {t("common.filtersModal.fieldsHeading")}
        </h4>

        <section>
          <div className="mb-4 h-60 scroll-y">
            {table
              .getAllColumns()
              ?.filter(
                (item) =>
                  item?.id?.toLowerCase() !== "actions" &&
                  item?.columnDef?.type !== undefined &&
                  item?.columnDef?.header
                    ?.toLowerCase()
                    .includes(search.toLowerCase()),
              )
              ?.map((field) => (
                <CheckboxField
                  key={field.id}
                  label={field.columnDef.header}
                  className="my-2"
                  checked={
                    !!filter.find(
                      (f) => f.accessorKey === field.columnDef.accessorKey,
                    )
                  }
                  onChange={(checked) => {
                    if (checked) {
                      handleAddFilter(field.columnDef);
                    } else {
                      handleDelete(field.columnDef.accessorKey);
                    }
                  }}
                />
              ))}
          </div>

          {filter.map((item) => (
            <div key={item.accessorKey} className="mt-4 space-y-2">
              {["text", "number"].includes(item.type) && (
                <div className="flex items-center gap-2">
                  <InputField
                    label={item.header}
                    type={item.type}
                    value={item.value}
                    onChange={(e) =>
                      handleChangeValue(item.accessorKey, e.target.value)
                    }
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="mt-4"
                    aria-label={t("common.filtersModal.removeFilter")}
                    onClick={() => handleDelete(item.accessorKey)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {/* Select */}
              {item.type === "select" && (
                <div className="flex items-center gap-2">
                  <div className="w-full">
                    <SelectInput
                      label={item.header}
                      options={item.options}
                      value={item.value}
                      className="w-full !h-[3rem]"
                      onChange={(value) =>
                        handleChangeValue(item.accessorKey, value)
                      }
                    />
                  </div>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="mt-4"
                    aria-label={t("common.filtersModal.removeFilter")}
                    onClick={() => handleDelete(item.accessorKey)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {/* Date */}
              {item.type === "date" && (
                <div className="flex items-center gap-2">
                  <div className="w-full">
                    <Calendar28
                      label={item.header}
                      value={item.value}
                      className={"h-[3rem] rounded-[1rem]"}
                      onChange={(date) =>
                        handleChangeValue(item.accessorKey, date)
                      }
                    />
                  </div>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="mt-4"
                    aria-label={t("common.filtersModal.removeFilter")}
                    onClick={() => handleDelete(item.accessorKey)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </section>
      </>
    </BaseModal>
  );
}
